/** `cubic-bezier(x1, y1, x2, y2)` as a function of progress, solved for x by Newton's method with a bisection fallback. */
function cubicBezier(x1: number, y1: number, x2: number, y2: number) {
  const curve = (a: number, b: number, t: number) =>
    3 * a * t * (1 - t) ** 2 + 3 * b * t * t * (1 - t) + t ** 3;
  const slope = (a: number, b: number, t: number) =>
    3 * a * (1 - t) ** 2 + 6 * (b - a) * t * (1 - t) + 3 * (1 - b) * t * t;
  return (x: number) => {
    if (x <= 0 || x >= 1) return Math.min(1, Math.max(0, x));
    let t = x;
    for (let i = 0; i < 8; i++) {
      const error = curve(x1, x2, t) - x;
      const d = slope(x1, x2, t);
      if (Math.abs(error) < 1e-5) return curve(y1, y2, t);
      if (Math.abs(d) < 1e-6) break;
      t -= error / d;
    }
    let [low, high] = [0, 1];
    t = x;
    while (high - low > 1e-5) {
      if (curve(x1, x2, t) < x) low = t;
      else high = t;
      t = (low + high) / 2;
    }
    return curve(y1, y2, t);
  };
}
const ease = cubicBezier(0.4, 0, 0.2, 1);
const INTERRUPTIONS = ["pointerdown", "wheel", "touchstart"] as const;

/** Scrolls `track` to `left` over `duration` ms with snapping switched off meanwhile, so the strip glides at the
 *  carousel's pace instead of the browser's; a pointer, wheel or touch hands the scroll back to the visitor. Returns a
 *  function that stops it; `onEnd` runs once when it ends either way. A duration of 0 (reduced motion) jumps. */
export function animateScroll(
  track: HTMLElement,
  left: number,
  duration: number,
  onEnd: () => void = () => {},
): () => void {
  const start = track.scrollLeft;
  const distance = left - start;
  if (duration <= 0 || Math.abs(distance) < 1) {
    track.scrollLeft = left;
    onEnd();
    return () => {};
  }
  const snap = track.style.scrollSnapType;
  track.style.scrollSnapType = "none";
  let frame = 0;
  let done = false;
  const began = performance.now();
  const stop = () => {
    if (done) return;
    done = true;
    cancelAnimationFrame(frame);
    track.style.scrollSnapType = snap;
    for (const type of INTERRUPTIONS) track.removeEventListener(type, stop);
    onEnd();
  };
  const step = (now: number) => {
    const progress = Math.min(1, (now - began) / duration);
    track.scrollLeft = start + distance * ease(progress);
    if (progress < 1) frame = requestAnimationFrame(step);
    else stop();
  };
  for (const type of INTERRUPTIONS)
    track.addEventListener(type, stop, { passive: true });
  frame = requestAnimationFrame(step);
  return stop;
}
